import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { filter } from 'rxjs/internal/operators/filter';
import {
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CONSTANTS_DETAILS } from '../../shared';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { SnackbarService } from '../snackbar/snackbar.service';
import {
  GDataResponse,
  RecipeReportPayload,
  RolesPrivilege,
} from '../../shared/model/recipes-interface';
import { TranslateService } from '@ngx-translate/core';
import { LicenseManager } from 'ag-grid-enterprise';
import { AesEncryptDecryptService } from '../decrypt/aes-encrypt-decrypt.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public readonly _destroying$ = new Subject<void>();
  public userRoles = new BehaviorSubject<RolesPrivilege>({} as RolesPrivilege);
  public dateFilter = new BehaviorSubject<RecipeReportPayload>(
    {} as RecipeReportPayload,
  );
  public hasRoles = true;
  constructor(
    private _authService: MsalService,
    private _msalBroadcastService: MsalBroadcastService,
    private _router: Router,
    private _http: HttpClient,
    private _snackbarService: SnackbarService,
    private _translate: TranslateService,
    private _aesEncryptDecryptService: AesEncryptDecryptService,
  ) {
    // Handle the Recipe redirection share via mail or URL for before and after login
    const beforeLogin = localStorage.getItem('isBeforeLogin');
    if (beforeLogin && beforeLogin !== '/') {
      localStorage.setItem('isAppRedirect', beforeLogin);
      localStorage.setItem('isBeforeLogin', '');
    } else {
      localStorage.setItem('isBeforeLogin', window.location.pathname);
      localStorage.setItem('isAppRedirect', window.location.pathname);
    }

    const saveFilter = localStorage.getItem('dateFilter');

    if (saveFilter) {
      this.dateFilter.next(JSON.parse(saveFilter) as RecipeReportPayload);
    }
  }

  /**
   * Initializes the authentication process by enabling account storage events
   *
   * @memberof AuthService
   */
  public authInitiator(): void {
    this._authService.instance.enableAccountStorageEvents();
    // Subscribe to the MSAL broadcast service to listen for account changes
    // This will handle events like account added, removed, login success, and login failure
    this._msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.LOGIN_FAILURE,
        ),
      )
      .subscribe((result: EventMessage) => {
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          localStorage.setItem('isBeforeLogin', '');
          this.appRedirection();
        }
      });
    // Subscribe to the broadcast service to listen for authentication status changes
    this._msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.isAuthenticated();
        this.getAndUpdateCurrentUserAccount();
      });
  }

  /**
   * Checks if the user is authenticated.
   *
   * @returns {boolean} True if the user is authenticated, otherwise false.
   * @memberof AuthService
   */
  public isAuthenticated(): boolean {
    return this._authService.instance.getAllAccounts().length > 0;
  }

  /**
   * Gets the set current user account information.
   *
   * @returns {void} The current user account information or null if not authenticated.
   * @memberof AuthService
   */
  public getAndUpdateCurrentUserAccount(): void {
    if (this.isAuthenticated()) {
      const activeAccount = this._authService.instance.getActiveAccount();
      if (
        !activeAccount &&
        this._authService.instance.getAllAccounts().length > 0
      ) {
        const accounts = this._authService.instance.getAllAccounts();
        this._authService.instance.setActiveAccount(accounts[0]);
      }
      this.getRolesPrivileges();
      this.getGridKey();
      this.appRedirection();
    } else {
      this.loginUser();
    }
  }

  /**
   * Logs in the user using a redirect method.
   *
   * @return {*}  {void}
   * @memberof AuthService
   */
  public loginUser(): void {
    this._authService.loginRedirect({
      scopes: environment.AUTH_CONFIG.tciu_scope,
    });
  }

  /**
   * Logs out the user and redirects to the post-logout URI.
   *
   * @memberof AuthService
   */
  public logoutUser(): void {
    this._authService.logoutRedirect({
      postLogoutRedirectUri: environment.AUTH_CONFIG.postLogoutRedirectUri,
      account: this._authService.instance.getActiveAccount(),
    });
    this.dateFilter.next({});
    localStorage.removeItem('dateFilter');
  }

  /**
   * Gets the user's email address from the current account.
   *
   * @return {*}  {string[]}
   * @memberof AuthService
   */
  public getAccountGroups() {
    return this._authService.instance.getActiveAccount()
      ? this._authService.instance.getActiveAccount()
      : null;
  }

  /**
   * Fetches the roles and privileges of the user from the server.
   *
   * @memberof AuthService
   */
  public getRolesPrivileges(): Promise<RolesPrivilege | []> {
    return lastValueFrom(
      this._http.get<RolesPrivilege>(
        environment.APP_BASE_URL + CONSTANTS_DETAILS.GET_USERPRIVILEGES_URL,
      ),
    )
      .then((response: RolesPrivilege) => {
        if (
          response &&
          response.roles &&
          response.roles.length &&
          response.privilegeIdentifiers &&
          response.privilegeIdentifiers.length
        ) {
          this.userRoles.next(response || []);
        } else {
          this.hasRoles = false;
          this._router.navigate(['/unauthorized']);
        }
        return response || [];
      })
      .catch(() => {
        this._snackbarService.open({
          message: this._translate.instant('errorInPrivilege'),
          type: 'error',
        });
        return [];
      });
  }

  /**
   * Fetches data for the people picker component.
   *
   * @memberof AuthService
   */
  public async getPeoplePickerData(user?: string): Promise<GDataResponse> {
    const header = {
      'Content-Type': 'application/json',
      Authorization: '',
    };
    const isToken = await lastValueFrom(
      this._authService.acquireTokenSilent({
        scopes: ['User.ReadBasic.All'],
      }),
    );
    if (isToken && isToken.accessToken) {
      header['Authorization'] = `Bearer ${isToken.accessToken}`;
    }
    return lastValueFrom(
      this._http.get<GDataResponse>(
        `https://graph.microsoft.com/v1.0/users?$filter=startswith(displayName,'${user}') or startswith(surname,'${user}')&$select=displayName,mail,surname`,
        { headers: header },
      ),
    ).catch(() => {
      this._snackbarService.open({
        message: this._translate.instant('peoplePickerError'),
        type: 'error',
      });
      return {} as GDataResponse;
    });
  }

  /**
   * Redirects the user to the application based on the current route.
   *
   * @private
   * @memberof AuthService
   */
  private appRedirection() {
    const appRedirect = localStorage.getItem('isAppRedirect');
    const recipeIdentifier = appRedirect
      ? `${appRedirect.split('/').pop()}`
      : '';
    if (appRedirect?.includes('recipe-details') && recipeIdentifier) {
      this._router.navigateByUrl(appRedirect, {
        state: { recipe: { recipeIdentifier: recipeIdentifier } },
      });
    } else if (recipeIdentifier) {
      this._router.navigate([appRedirect]);
    } else {
      this._router.navigate(['/recipe']);
    }
  }

  /**
   * Gets the grid key.
   *
   * @return {*}  {Promise<string>}
   * @memberof RecipeService
   */
  public async getGridKey(): Promise<string> {
    return lastValueFrom(
      this._http.get<string>(
        environment.APP_BASE_URL + CONSTANTS_DETAILS.GET_AG_GRID_KEY,
        { responseType: 'text' as 'json' },
      ),
    )
      .then(async (gridKey) => {
        const decryptedKey =
          await this._aesEncryptDecryptService.decryptAES(gridKey);
        LicenseManager.setLicenseKey(decryptedKey);
        return decryptedKey;
      })
      .catch(() => {
        this._snackbarService.open({
          message: this._translate.instant('errorInvalidGridKey'),
          type: 'error',
        });
        return '';
      });
  }
  /**
   * Set the time filter for KPI dashboard
   *
   * @memberof AuthService
   */
  public setFilter(timeFilter: RecipeReportPayload) {
    this.dateFilter.next(timeFilter);
    localStorage.setItem('dateFilter', JSON.stringify(timeFilter));
  }
}
